// InactiveCustomers.js - UI matched with Users Management page + City filter + Dynamic City Counts
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './InactiveCustomers.css';
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaSearch,
  FaCheckDouble,
  FaBuilding,
  FaMapMarkerAlt,
  FaUsersSlash
} from "react-icons/fa";
import { RiDeleteBinFill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

// Last loaded customers / cities of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const IC_CACHE_PREFIX = 'inactive_cache_';
const IC_CITIES_PREFIX = 'inactive_cities_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

// =========================
// AUTH TOKEN
// =========================
const getAuthToken = () => {
  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (const key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
        if (parsed.token && typeof parsed.token === 'object') {
          return parsed.token.access || parsed.token.token || parsed.token.access_token;
        }
      } catch (e) {
        return storedData;
      }
    }
  }
  return null;
};

const InactiveCustomers = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The shop is known from the very first render (from the URL), and the saved
  // list of that shop is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const urlMatch = location.pathname.match(/\/shop\/(\d+)/);
    const startShopId = urlMatch
      ? urlMatch[1]
      : (localStorage.getItem('selectedShopId') || localStorage.getItem('selected_shop_id') || '');
    const cachedData = startShopId ? readJson(`${IC_CACHE_PREFIX}${startShopId}`) : null;
    const cachedCities = startShopId ? readJson(`${IC_CITIES_PREFIX}${startShopId}`) : null;
    initialRef.current = {
      shopId: startShopId ? String(startShopId) : '',
      data: Array.isArray(cachedData) ? cachedData : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null
    };
  }

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage] = useState(10);
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  // All inactive customers of the shop (all cities). City filter / search work on this list.
  const [masterCustomers, setMasterCustomers] = useState(initialRef.current.data || []);
  // True only while there is nothing to show yet (so "No inactive customers found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.data);
  const [error, setError] = useState(null);

  const [selectedShopId, setSelectedShopId] = useState(initialRef.current.shopId);

  // ✅ City filter dropdown (header)
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'Active'
  });

  const prevShopRef = useRef(initialRef.current.shopId);
  // The shop the list in `masterCustomers` belongs to (the saved copy is only written for it)
  const dataShopRef = useRef(initialRef.current.data ? initialRef.current.shopId : null);
  const customersRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);

  const getShopIdFromUrl = () => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    return match ? match[1] : null;
  };

  // =========================
  // SHOP: URL me na ho to saved shop / pehli shop (list sirf tab chahiye jab koi shop pata hi na ho)
  // =========================
  useEffect(() => {
    const urlShopId = getShopIdFromUrl();
    if (urlShopId) return;

    if (selectedShopId) {
      navigate(`/shop/${selectedShopId}/inactive-customers`, { replace: true });
      return;
    }

    (async () => {
      try {
        const token = getAuthToken();
        if (!token) {
          setIsLoading(false);
          return;
        }
        const response = await fetch(`${API_BASE_URL}/user/shops/`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch shops');
        const raw = await response.json();
        const data = Array.isArray(raw) ? raw : (raw.results || raw.shops || []);

        if (data.length > 0) {
          const firstShopId = data[0].shop_id || data[0].id || data[0]._id;
          if (firstShopId) {
            const idStr = String(firstShopId);
            localStorage.setItem('selectedShopId', idStr);
            setSelectedShopId(idStr);
            navigate(`/shop/${idStr}/inactive-customers`, { replace: true });
            return;
          }
        }
        setIsLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to initialize.');
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // WATCH URL SHOP
  // =========================
  useEffect(() => {
    const urlShopId = getShopIdFromUrl();
    if (urlShopId && urlShopId !== selectedShopId) {
      setSelectedShopId(urlShopId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // =========================
  // FETCH CITIES (header dropdown)
  // =========================
  const fetchFilterCities = useCallback(async (shopId) => {
    if (!shopId) return;
    const requestId = ++citiesRequestRef.current;
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/user/shop-cities/?shop_id=${shopId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (requestId !== citiesRequestRef.current) return;

      let citiesData = [];
      if (response.ok) {
        const data = await response.json();
        if (requestId !== citiesRequestRef.current) return;
        if (data) {
          if (Array.isArray(data.cities)) {
            citiesData = data.cities;
          } else if (Array.isArray(data)) {
            citiesData = data;
          } else if (typeof data.cities === 'string') {
            try {
              citiesData = JSON.parse(data.cities);
            } catch {
              citiesData = data.cities.split(',').map((c) => c.trim()).filter((c) => c);
            }
          }
        }
      }

      setFilterCities(citiesData);
      if (citiesData.length) writeJson(`${IC_CITIES_PREFIX}${shopId}`, citiesData);
    } catch (err) {
      if (requestId !== citiesRequestRef.current) return;
      console.error('Error fetching shop cities:', err);
    }
  }, []);

  // =========================
  // FETCH INACTIVE CUSTOMERS (all cities of the shop)
  // =========================
  const fetchInactiveCustomers = useCallback(async (shopId) => {
    if (!shopId) return;
    const shopKey = String(shopId);
    // Only the latest request is allowed to update the screen
    const requestId = ++customersRequestRef.current;

    try {
      const token = getAuthToken();
      if (!token) throw new Error('No authentication token found.');

      const response = await fetch(`${API_BASE_URL}/user/inactive-customers/?shop_id=${shopKey}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const raw = await response.json();
      if (requestId !== customersRequestRef.current) return;

      const data = Array.isArray(raw) ? raw : (raw.results || raw.customers || []);

      const formattedCustomers = data.map((customer) => ({
        id: customer.id,
        firstName: customer.first_Name || '',
        lastName: customer.last_Name || '',
        email: customer.email || '',
        phone: customer.phone_Number || '',
        shopName: customer.shop_name || '',
        shopId: customer.shop || shopKey,
        selectedCity: customer.selected_city || '',
        status: customer.status || 'Inactive',
        customStatus: customer.custom_status || '',
        isActive: customer.is_active
      }));

      dataShopRef.current = shopKey;
      setMasterCustomers(formattedCustomers);
      setError(null);
    } catch (err) {
      if (requestId !== customersRequestRef.current) return;
      // Agar saved list dikh rahi hai to error screen nahi, wohi list rehne do
      if (dataShopRef.current !== shopKey) {
        setError(err.message || 'Failed to fetch inactive customers.');
      }
    } finally {
      if (requestId === customersRequestRef.current) setIsLoading(false);
    }
  }, []);

  // Data of the shop: show the saved list of the shop immediately, then refresh it.
  // Customers and cities load together instead of one after another.
  useEffect(() => {
    if (!selectedShopId) return;
    const shopId = String(selectedShopId);

    if (prevShopRef.current !== shopId) {
      prevShopRef.current = shopId;
      const cachedData = readJson(`${IC_CACHE_PREFIX}${shopId}`);
      const cachedCities = readJson(`${IC_CITIES_PREFIX}${shopId}`);
      if (Array.isArray(cachedData)) {
        dataShopRef.current = shopId;
        setMasterCustomers(cachedData);
        setIsLoading(false);
      } else {
        dataShopRef.current = null;
        setMasterCustomers([]);
        setIsLoading(true);
      }
      setFilterCities(Array.isArray(cachedCities) ? cachedCities : []);
      setSelectedFilterCity('');
      setSelectedCustomers([]);
      setCurrentPage(1);
      setError(null);
    }

    fetchFilterCities(shopId);
    fetchInactiveCustomers(shopId);
  }, [selectedShopId, fetchFilterCities, fetchInactiveCustomers]);

  // The saved copy of the list is kept up to date (also after edit / delete)
  useEffect(() => {
    if (!selectedShopId || dataShopRef.current !== String(selectedShopId)) return;
    writeJson(`${IC_CACHE_PREFIX}${selectedShopId}`, masterCustomers);
  }, [masterCustomers, selectedShopId]);

  // City ya search badle to pehle page par jao aur selection saaf karo
  useEffect(() => {
    setCurrentPage(1);
    setSelectedCustomers([]);
  }, [selectedFilterCity, searchTerm]);

  const handleFilterCityChange = (e) => {
    setSelectedFilterCity(e.target.value);
  };

  // Server ki city list na aaye to customers ki apni cities use hoti hain
  const cityOptions = useMemo(() => {
    if (filterCities.length > 0) return filterCities;
    return [...new Set(masterCustomers.map((c) => c.selectedCity).filter(Boolean))].sort();
  }, [filterCities, masterCustomers]);

  // ✅ Calculate total inactive customers for "All Cities"
  const allCitiesCount = masterCustomers.length;

  // ✅ Helper to calculate count for a specific city
  const getCityCount = (cityName) =>
    masterCustomers.filter((customer) => customer.selectedCity === cityName).length;

  // =========================
  // CITY FILTER + SEARCH (instant, no server call)
  // =========================
  const filteredCustomers = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();

    return masterCustomers.filter((customer) => {
      if (selectedFilterCity && customer.selectedCity !== selectedFilterCity) return false;

      const fullName = `${customer.firstName} ${customer.lastName}`.toLowerCase();
      const email = (customer.email || '').toLowerCase();
      const phone = customer.phone || '';
      const status = (customer.status || '').toLowerCase();
      const city = (customer.selectedCity || '').toLowerCase();

      return (
        fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        phone.includes(searchTerm) ||
        status.includes(searchLower) ||
        city.includes(searchLower)
      );
    });
  }, [masterCustomers, selectedFilterCity, searchTerm]);

  // =========================
  // PAGINATION
  // =========================
  const totalCustomers = filteredCustomers.length;
  const totalPages = Math.ceil(totalCustomers / rowsPerPage);
  const safePage = Math.min(currentPage, Math.max(totalPages, 1));
  const indexOfLastCustomer = safePage * rowsPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - rowsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);

  const goToPreviousPage = () => {
    if (safePage > 1) setCurrentPage(safePage - 1);
  };
  const goToNextPage = () => {
    if (safePage < totalPages) setCurrentPage(safePage + 1);
  };

  // =========================
  // SELECT ALL / SELECT
  // =========================
  const handleSelectAll = () => {
    if (selectedCustomers.length === currentCustomers.length && currentCustomers.length > 0) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(currentCustomers.map((customer) => customer.id));
    }
  };

  const handleSelectCustomer = (id) => {
    if (selectedCustomers.includes(id)) {
      setSelectedCustomers(selectedCustomers.filter((customerId) => customerId !== id));
    } else {
      setSelectedCustomers([...selectedCustomers, id]);
    }
  };

  // =========================
  // EDIT
  // =========================
  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setEditFormData({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      status: customer.status || 'Active'
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    if (isSaving) return;
    setIsEditModalOpen(false);
    setEditingCustomer(null);
    setEditFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      status: 'Active'
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCustomer) return;

    if (!editFormData.firstName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'First Name is required.',
        confirmButtonColor: '#004aad'
      });
      return;
    }
    if (!editFormData.email.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Email is required.',
        confirmButtonColor: '#004aad'
      });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(editFormData.email.trim())) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please enter a valid email address.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    try {
      setIsSaving(true);
      Swal.fire({
        title: 'Updating Customer...',
        text: 'Please wait',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => { Swal.showLoading(); }
      });

      const token = getAuthToken();
      if (!token) {
        Swal.close();
        throw new Error('No authentication token found.');
      }

      // ✅ Users page wala SAME endpoint (UserDetailView): is_active -> type auto sync
      const isActiveStatus = editFormData.status === 'Active';

      const response = await fetch(
        `${API_BASE_URL}/user/users/${editingCustomer.id}/`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            first_Name: editFormData.firstName.trim(),
            last_Name: editFormData.lastName.trim(),
            email: editFormData.email.trim(),
            phone_Number: editFormData.phone.trim(),
            is_active: isActiveStatus
          })
        }
      );

      Swal.close();

      if (!response.ok) {
        let errorMessage = 'Failed to update customer.';
        try {
          const errorData = await response.json();
          if (errorData.detail) errorMessage = errorData.detail;
          else if (errorData.error) errorMessage = errorData.error;
          else if (typeof errorData === 'object') {
            errorMessage = Object.values(errorData).flat().join(', ');
          }
        } catch (e) {}
        throw new Error(errorMessage);
      }

      // ✅ List me foran badlo (activated customer nikal jata hai), phir peeche se taza data
      const editedId = editingCustomer.id;
      setMasterCustomers((prev) =>
        isActiveStatus
          ? prev.filter((c) => c.id !== editedId)
          : prev.map((c) =>
              c.id === editedId
                ? {
                    ...c,
                    firstName: editFormData.firstName.trim(),
                    lastName: editFormData.lastName.trim(),
                    email: editFormData.email.trim(),
                    phone: editFormData.phone.trim(),
                    status: 'Inactive'
                  }
                : c
            )
      );
      fetchInactiveCustomers(selectedShopId);

      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: isActiveStatus
          ? 'Customer activated successfully. It will now appear in Active Customers list.'
          : 'Customer updated successfully.',
        confirmButtonColor: '#004aad',
        timer: 2000
      });

      handleCloseEdit();
    } catch (err) {
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: `Failed to update: ${err.message}`,
        confirmButtonColor: '#004aad'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // =========================
  // DELETE
  // =========================
  const handleDelete = async (id) => {
    const customer = masterCustomers.find((c) => c.id === id);
    const customerName = customer
      ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'this customer'
      : 'this customer';

    const result = await Swal.fire({
      title: 'Delete Customer?',
      html: `Are you sure you want to delete <strong>${customerName}</strong>?<br/><small style="color:#dc3545;">This action cannot be undone.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: 'Deleting...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });

      const token = getAuthToken();
      if (!token) {
        Swal.close();
        throw new Error('No authentication token found.');
      }

      const response = await fetch(
        `${API_BASE_URL}/user/delete/${id}/`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      Swal.close();

      if (!response.ok) {
        throw new Error(`Failed to delete customer. Status: ${response.status}`);
      }

      setMasterCustomers((prev) => prev.filter((c) => c.id !== id));
      setSelectedCustomers((prev) => prev.filter((customerId) => customerId !== id));
      fetchInactiveCustomers(selectedShopId);

      await Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `${customerName} has been deleted successfully.`,
        confirmButtonColor: '#004aad',
        timer: 2000
      });
    } catch (err) {
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: `Failed to delete: ${err.message}`,
        confirmButtonColor: '#004aad'
      });
    }
  };

  if (error && masterCustomers.length === 0 && !isLoading) {
    return (
      <div className="inactive-customers-page">
        <div className="inactive-error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button
            className="inactive-retry-btn"
            onClick={() => {
              setError(null);
              setIsLoading(true);
              fetchInactiveCustomers(selectedShopId);
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inactive-customers-page">

      {/* HEADER - Users Management style */}
      <div className="inactive-customers-header-section">
        <div className="inactive-customers-header-left">
          <FaUsersSlash className="inactive-customers-header-icon" />
          <h2 className="inactive-customers-header-title">Inactive Customers</h2>

          {/* ✅ Dynamic City Badge with Count */}
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity || 'All Cities'} ({selectedFilterCity ? getCityCount(selectedFilterCity) : allCitiesCount})
            </span>
          </span>
        </div>

        <div className="inactive-customers-header-right">
          {/* ✅ City Dropdown with Dynamic Counts */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={cityOptions.length === 0}
            >
              <option value="">
                All Cities ({allCitiesCount})
              </option>
              {cityOptions.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({getCityCount(city)})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          {/* Search */}
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search inactive customers..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>
              {selectedCustomers.length === currentCustomers.length && currentCustomers.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </span>
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="inactive-customers-table-container">
        <div className="inactive-customers-table-wrapper">
          <table className="inactive-customers-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={
                      selectedCustomers.length === currentCustomers.length &&
                      currentCustomers.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>City</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Nothing is shown until the first load is done, so "No inactive customers found" never flashes */}
              {isLoading ? (
                <tr>
                  <td colSpan="7" style={{ height: '200px' }}></td>
                </tr>
              ) : currentCustomers.length > 0 ? (
                currentCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className={selectedCustomers.includes(customer.id) ? 'selected' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={() => handleSelectCustomer(customer.id)}
                      />
                    </td>
                    <td>
                      <div className="customer-name-cell">
                        {customer.firstName} {customer.lastName}
                      </div>
                    </td>
                    <td>{customer.email}</td>
                    <td>{customer.phone}</td>
                    <td>
                      <span className="user-city-cell">
                        {customer.selectedCity || '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${customer.status === 'Active' ? 'active' : 'inactive'}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="actions-cell-inner">
                        <button
                          className="edit-customer-btn"
                          onClick={() => handleEdit(customer)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="delete-customer-btn"
                          onClick={() => handleDelete(customer.id)}
                          title="Delete"
                        >
                          <RiDeleteBinFill />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="no-data-cell">
                    <FaBuilding className="no-data-icon" />
                    <p>
                      {selectedFilterCity
                        ? `No inactive customers found in ${selectedFilterCity}.`
                        : 'No inactive customers found.'}
                    </p>
                    {selectedFilterCity && (
                      <button
                        className="clear-city-filter-btn"
                        onClick={() => setSelectedFilterCity('')}
                      >
                        Clear City Filter
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}
      <div className="pagination-section">
        <div className="pagination-wrapper">
          <div className="pagination-info">
            {totalCustomers > 0
              ? `${indexOfFirstCustomer + 1}-${Math.min(indexOfLastCustomer, totalCustomers)} of ${totalCustomers}`
              : '0-0 of 0'}
          </div>
          <button
            className="page-btn"
            onClick={goToPreviousPage}
            disabled={safePage === 1}
          >
            <FaChevronLeft /> Previous
          </button>
          <span className="current-page">
            Page {safePage} of {totalPages || 1}
          </span>
          <button
            className="page-btn"
            onClick={goToNextPage}
            disabled={safePage === totalPages || totalPages === 0}
          >
            Next <FaChevronRight />
          </button>
        </div>
      </div>

      {/* EDIT MODAL */}
      {isEditModalOpen && (
        <div className="modal-overlay" onClick={handleCloseEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Customer</h3>
              <button
                className="modal-close"
                onClick={handleCloseEdit}
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.firstName}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, firstName: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.lastName}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, lastName: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={editFormData.email}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, email: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.phone}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, phone: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-input"
                  value={editFormData.status}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, status: e.target.value })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCloseEdit}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSaveEdit}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InactiveCustomers;